import * as React from 'react';
import { logError } from '../../utils/logger';

interface ErrorBoundaryProps {
  /**
   * Child content to render as a fallback if there is an error.
   */
  fallback: React.ReactNode;
  /**
   * Child content to render in the happy case when there is no error.
   */
  children: React.ReactNode;
  /**
   * Observer callback to notify if there is an error.
   * Note that this does not override the error boundary's fallback or reporting,
   * but does allow for extra side effects or visibility.
   */
  onError?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // always log the caught error regardless of other handlers
    logError(error, info);
    // if there is an observer callback registered then call it
    if (this.props.onError) {
      this.props.onError();
    }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
